import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  GitBranch,
  ShieldCheck,
  Timer,
  TrendingUp,
  Users,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import apiClient from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { POD_LABELS, STATUS_LABELS } from "@/lib/constants";
import type { Pod } from "@/lib/types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface SummaryData {
  total: number;
  by_status: Record<string, number>;
  cycle_time_avg_days: number;
  requests_this_week: number;
  requests_per_week: number;
}

interface FunnelData {
  [key: string]: { count: number; conversion_percent: number };
}

interface PodPerformance {
  [key: string]: {
    total: number;
    completed: number;
    completion_percent: number;
    in_progress: number;
    awaiting_action: number;
    cycle_time_days: number;
    velocity_per_week: number;
  };
}

interface Escalation {
  reference_id: string;
  title: string;
  status: string;
  days_stuck: number;
  last_updated: string;
  submitter_email: string;
}

interface EscalationData {
  total: number;
  threshold_days: number;
  escalations: Escalation[];
}

interface TrendData {
  [key: string]: number;
}

interface AgingBucket {
  count: number;
  label: string;
  days_range: [number, number];
}

interface StaleRequest {
  id: string;
  reference_id: string;
  title: string;
  status: string;
  days_idle: number;
  pod: string;
}

interface AgingData {
  fresh: AgingBucket;
  aging: AgingBucket;
  stale: AgingBucket;
  stale_requests: StaleRequest[];
}

// ── Leadership feature types ───────────────────────────────────────────────────

interface TFRPriority {
  avg_hours: number;
  avg_display: string;
  responded_count: number;
  sla_threshold_hours: number;
}
interface TFRData {
  overall_avg_hours: number;
  overall_display: string;
  by_priority: Record<string, TFRPriority>;
}

interface SLAPriority {
  total: number;
  within_sla: number;
  compliance_pct: number | null;
  sla_threshold_hours: number;
  sla_label: string;
}
interface SLAData {
  overall_compliance_pct: number | null;
  by_priority: Record<string, SLAPriority>;
}

interface WorkloadMember {
  email: string;
  name: string;
  total: number;
  in_progress: number;
  awaiting: number;
}
interface WorkloadData {
  members: WorkloadMember[];
  unclaimed: { total: number; awaiting: number };
  total_active: number;
}

interface TypeTrendData {
  [week: string]: { Feature: number; Defect: number };
}

interface CycleTimePriority {
  avg_days: number | null;
  min_days: number | null;
  max_days: number | null;
  sample_size: number;
}
interface CycleTimeData {
  by_priority: Record<string, CycleTimePriority>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fetchAnalytics =
  <T,>(endpoint: string) =>
  async (): Promise<T> => {
    const res = await apiClient.get(endpoint);
    return res.data as T;
  };

// Accent color per pod key
const POD_COLORS: Record<string, string> = {
  charging_stations: "#2563eb",
  driver_mobile_app: "#059669",
  payments_billing: "#dc2626",
  data_analytics: "#7c3aed",
  system_infrastructure: "#ea580c",
  enterprise_fleet: "#0891b2",
};

function podColor(key: string): string {
  return POD_COLORS[key] ?? "#64748b";
}

// Funnel status colors
const FUNNEL_COLORS: Record<string, string> = {
  Submitted: "#3b82f6",
  InReview: "#eab308",
  AwaitingInfo: "#f97316",
  InfoReceived: "#14b8a6",
  Approved: "#10b981",
  InProgress: "#6366f1",
  Completed: "#22c55e",
  Rejected: "#ef4444",
  Cancelled: "#94a3b8",
};

// ── SVG sub-components ────────────────────────────────────────────────────────

/** Sparkline area chart — purely decorative shape derived from `points` (0–100 scale). */
function Sparkline({ points, color }: { points: number[]; color: string }) {
  const H = 28;
  const W = 120;
  const n = points.length;
  if (n < 2) return null;
  const max = Math.max(...points, 1);
  const xs = points.map((_, i) => (i / (n - 1)) * W);
  const ys = points.map((v) => H - (v / max) * (H - 4));
  const linePts = xs
    .map((x, i) => `${i === 0 ? "M" : "L"}${x},${ys[i]}`)
    .join(" ");
  const areaPath = `${linePts} L${xs[n - 1]},${H} L0,${H} Z`;
  const gradId = `sg-${color.replace("#", "")}`;
  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-2.5"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={xs[n - 1]} cy={ys[n - 1]} r="2.5" fill={color} />
    </svg>
  );
}

/** SVG donut chart showing completion percentage. */
function DonutChart({ pct, color }: { pct: number; color: string }) {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(1, pct / 100)) * circ;
  const gap = circ - dash;
  const pctColor = pct >= 50 ? color : pct > 0 ? "#f59e0b" : "#94a3b8";
  return (
    <div className="relative w-[72px] h-[72px] shrink-0">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          opacity={0.1}
        />
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeDasharray={`${dash.toFixed(1)} ${gap.toFixed(1)}`}
          strokeLinecap="round"
          style={{ transformOrigin: "36px 36px", transform: "rotate(-90deg)" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold"
        style={{ color: pctColor }}
      >
        {pct}%
      </div>
    </div>
  );
}

/** SVG area chart for weekly trend. */
function TrendChart({ data }: { data: { week: string; requests: number }[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const W = 380;
  const H = 190;
  const padL = 28;
  const padR = 12;
  const padT = 14;
  const padB = 32;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const n = data.length;
  if (n < 2)
    return (
      <div className="h-[190px] flex items-center justify-center text-xs text-muted-foreground">
        Not enough data
      </div>
    );

  const maxVal = Math.max(...data.map((d) => d.requests), 1);
  const xStep = chartW / (n - 1);
  const pts = data.map((d, i) => ({
    x: padL + i * xStep,
    y: padT + chartH - (d.requests / maxVal) * chartH,
  }));

  const gridLines = [
    0,
    Math.round(maxVal / 3),
    Math.round((2 * maxVal) / 3),
    maxVal,
  ];
  const linePts = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePts} L${pts[n - 1].x.toFixed(1)},${padT + chartH} L${padL},${padT + chartH} Z`;

  return (
    <div className="overflow-x-auto overflow-y-visible">
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        style={{ overflow: "visible" }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        <defs>
          <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
          </linearGradient>
        </defs>
        {gridLines.map((v, i) => {
          const y = padT + chartH - (v / maxVal) * chartH;
          return (
            <g key={i}>
              <line
                x1={padL}
                y1={y}
                x2={W - padR}
                y2={y}
                stroke="currentColor"
                strokeWidth={0.5}
                opacity={0.12}
              />
              <text
                x={padL - 4}
                y={y + 4}
                textAnchor="end"
                fontSize={9}
                fill="currentColor"
                opacity={0.5}
              >
                {v}
              </text>
            </g>
          );
        })}
        <path d={areaPath} fill="url(#trendGrad)" />
        <path
          d={linePts}
          fill="none"
          stroke="#2563eb"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((d, i) => {
          const isHovered = hoveredIdx === i;
          const tipW = 88;
          const tipH = 38;
          const tipX = Math.min(
            Math.max(pts[i].x - tipW / 2, padL),
            W - padR - tipW,
          );
          // flip below the point if tooltip would be clipped above the SVG
          const tipAbove = pts[i].y - tipH - 10;
          const tipY = tipAbove < 0 ? pts[i].y + 10 : tipAbove;
          return (
            <g key={i}>
              <text
                x={pts[i].x}
                y={H - padB + 14}
                textAnchor="middle"
                fontSize={9}
                fill="currentColor"
                opacity={isHovered ? 0.9 : 0.5}
                fontWeight={isHovered ? "bold" : "normal"}
              >
                {d.week}
              </text>
              {/* invisible hit area for easier hover */}
              <rect
                x={pts[i].x - xStep / 2}
                y={padT}
                width={xStep}
                height={chartH + padB}
                fill="transparent"
                onMouseEnter={() => setHoveredIdx(i)}
                style={{ cursor: "crosshair" }}
              />
              <circle
                cx={pts[i].x}
                cy={pts[i].y}
                r={isHovered ? 5 : d.requests > 0 ? 3 : 2}
                fill="#2563eb"
                opacity={d.requests > 0 ? 1 : 0.35}
                style={{ pointerEvents: "none" }}
              />
              {isHovered && (
                <g style={{ pointerEvents: "none" }}>
                  <rect
                    x={tipX}
                    y={tipY}
                    width={tipW}
                    height={tipH}
                    rx={5}
                    fill="#1e293b"
                    stroke="#475569"
                    strokeWidth={1}
                    opacity={0.95}
                  />
                  <text
                    x={tipX + tipW / 2}
                    y={tipY + 13}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#94a3b8"
                  >
                    {d.week}
                  </text>
                  <text
                    x={tipX + tipW / 2}
                    y={tipY + 28}
                    textAnchor="middle"
                    fontSize={13}
                    fontWeight="bold"
                    fill="#f8fafc"
                  >
                    {d.requests} request{d.requests !== 1 ? "s" : ""}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

export function PMSummaryPage() {
  const navigate = useNavigate();
  const { isPM, isReviewer, isLoading: authLoading, user } = useAuth();

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isPM && !isReviewer) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">
            Only PMs and reviewers can view the summary dashboard
          </p>
        </div>
      </div>
    );
  }

  const authReady = !!user && (isPM || isReviewer);

  const analyticsQuery = <T,>(key: string, endpoint: string) => ({
    queryKey: ["analytics", key],
    queryFn: fetchAnalytics<T>(endpoint),
    refetchInterval: 30000,
    enabled: authReady,
  });

  const { data: summary } = useQuery(
    analyticsQuery<SummaryData>("summary", "/analytics/summary"),
  );
  const { data: funnel } = useQuery(
    analyticsQuery<FunnelData>("funnel", "/analytics/flow"),
  );
  const { data: podPerf } = useQuery(
    analyticsQuery<PodPerformance>(
      "pod-performance",
      "/analytics/pod-performance",
    ),
  );
  const { data: escalations } = useQuery(
    analyticsQuery<EscalationData>("escalations", "/analytics/escalations"),
  );
  const { data: trend } = useQuery(
    analyticsQuery<TrendData>("trend", "/analytics/trend"),
  );
  const { data: aging } = useQuery(
    analyticsQuery<AgingData>("aging", "/analytics/request-aging"),
  );
  const { data: tfr } = useQuery(
    analyticsQuery<TFRData>("tfr", "/analytics/time-to-first-response"),
  );
  const { data: sla } = useQuery(
    analyticsQuery<SLAData>("sla", "/analytics/sla-compliance"),
  );
  const { data: workload } = useQuery(
    analyticsQuery<WorkloadData>("workload", "/analytics/workload-balance"),
  );
  const { data: typeTrend } = useQuery(
    analyticsQuery<TypeTrendData>("type-trend", "/analytics/type-trend"),
  );
  const { data: cycleTime } = useQuery(
    analyticsQuery<CycleTimeData>(
      "priority-cycle-time",
      "/analytics/priority-cycle-time",
    ),
  );

  // Trend chart data
  const trendChartData = useMemo(() => {
    if (!trend) return [];
    return Object.entries(trend).map(([week, count]) => ({
      week,
      requests: count as number,
    }));
  }, [trend]);

  // Sparkline points for "This Week" using trend data
  const trendPoints = useMemo(
    () => trendChartData.map((d) => d.requests),
    [trendChartData],
  );

  // Funnel rows sorted by a logical order
  const funnelRows = useMemo(() => {
    if (!funnel) return [];
    const ORDER = [
      "Submitted",
      "InReview",
      "AwaitingInfo",
      "InfoReceived",
      "Approved",
      "InProgress",
      "Completed",
      "Rejected",
      "Cancelled",
    ];
    const totalCount = Object.values(funnel).reduce(
      (sum, d) => sum + d.count,
      0,
    );
    const max = Math.max(...Object.values(funnel).map((d) => d.count), 1);
    return ORDER.filter((s) => funnel[s]?.count > 0).map((s) => ({
      status: s,
      label: (
        STATUS_LABELS[s as keyof typeof STATUS_LABELS] ?? s
      ).toUpperCase(),
      count: funnel[s].count,
      pct:
        totalCount > 0
          ? Math.round((funnel[s].count / totalCount) * 1000) / 10
          : 0,
      color: FUNNEL_COLORS[s] ?? "#64748b",
      widthPct: (funnel[s].count / max) * 100,
    }));
  }, [funnel]);

  // Aging health bar percentages
  const agingTotal = aging
    ? aging.fresh.count + aging.aging.count + aging.stale.count || 1
    : 1;
  const freshPct = aging ? (aging.fresh.count / agingTotal) * 100 : 0;
  const agingPct = aging ? (aging.aging.count / agingTotal) * 100 : 0;
  const stalePct = aging ? (aging.stale.count / agingTotal) * 100 : 0;

  // CSV export
  const handleExport = () => {
    const timestamp = new Date().toISOString().split("T")[0];
    const csv = [
      ["PM Summary Dashboard Export", timestamp],
      [],
      ["OVERALL METRICS"],
      ["Total Requests", summary?.total || 0],
      ["Approved & Ready", summary?.by_status.approved || 0],
      ["Avg Cycle Time (days)", summary?.cycle_time_avg_days || 0],
      ["Requests This Week", summary?.requests_this_week || 0],
      [],
      ["REQUEST STATUS BREAKDOWN"],
      ...Object.entries(summary?.by_status || {}).map(([s, c]) => [s, c]),
      [],
      ["REQUEST FLOW FUNNEL"],
      ["Status", "Count", "Conversion %"],
      ...Object.entries(funnel || {}).map(([s, d]) => [
        s,
        d.count,
        d.conversion_percent,
      ]),
      [],
      ["REQUEST AGING"],
      ["Bucket", "Count", "Days Range"],
      ["Fresh", aging?.fresh.count || 0, aging?.fresh.label || "0–30 days"],
      ["Aging", aging?.aging.count || 0, aging?.aging.label || "30–60 days"],
      ["Stale", aging?.stale.count || 0, aging?.stale.label || "60+ days"],
      [],
      ...(aging?.stale_requests?.length
        ? [
            ["OLDEST STALE REQUESTS"],
            ["Reference ID", "Title", "Status", "Days Idle", "Pod"],
            ...aging.stale_requests.map((r) => [
              r.reference_id,
              r.title,
              r.status,
              r.days_idle,
              r.pod,
            ]),
            [],
          ]
        : []),
      ["TEAM PERFORMANCE"],
      [
        "Pod",
        "Total",
        "Completed",
        "Completion %",
        "In Progress",
        "Cycle Time (days)",
        "Velocity/Week",
      ],
      ...Object.entries(podPerf || {}).map(([pod, p]) => [
        pod,
        p.total,
        p.completed,
        p.completion_percent,
        p.in_progress,
        p.cycle_time_days,
        p.velocity_per_week,
      ]),
    ]
      .map((row) => row.map((c) => `"${c}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pm-dashboard-${timestamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            PM Summary Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Request health overview · updates every 30s
          </p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors shrink-0"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {/* Total Requests */}
        <div
          className="rounded-xl border bg-card px-4 pt-4 pb-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate("/dashboard")}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950">
              <FileText className="h-4.5 w-4.5 text-blue-600" />
            </div>
          </div>
          <p className="text-[26px] font-extrabold leading-none tracking-tight">
            {summary?.total ?? "—"}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Total Requests
          </p>
          <Sparkline points={[5, 8, 7, 10, 12, 14, 15, 16]} color="#2563eb" />
        </div>

        {/* Approved & Ready */}
        <div
          className="rounded-xl border bg-card px-4 pt-4 pb-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate("/dashboard?status=Approved")}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950">
              <CheckCircle2 className="h-4.5 w-4.5 text-green-600" />
            </div>
          </div>
          <p className="text-[26px] font-extrabold leading-none tracking-tight text-green-600">
            {summary?.by_status?.approved ??
              summary?.by_status?.Approved ??
              "—"}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Approved &amp; Ready
          </p>
          <p className="text-[11px] font-medium text-green-600 mt-0.5">
            Ready for development
          </p>
          <Sparkline points={[1, 1, 2, 2, 3, 4, 4, 5]} color="#10b981" />
        </div>

        {/* Avg Cycle Time */}
        <div
          className="rounded-xl border bg-card px-4 pt-4 pb-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate("/dashboard")}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-950">
              <Clock className="h-4.5 w-4.5 text-amber-600" />
            </div>
          </div>
          <p className="text-[26px] font-extrabold leading-none tracking-tight">
            {summary?.cycle_time_avg_days ?? "—"}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Avg Cycle Time
          </p>
          <p className="text-[11px] font-medium text-muted-foreground mt-0.5">
            days to completion
          </p>
          <Sparkline points={[14, 13, 12, 11, 10, 10, 9, 9]} color="#f59e0b" />
        </div>

        {/* This Week */}
        <div
          className="rounded-xl border bg-card px-4 pt-4 pb-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate("/dashboard")}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-950">
              <TrendingUp className="h-4.5 w-4.5 text-violet-600" />
            </div>
          </div>
          <p className="text-[26px] font-extrabold leading-none tracking-tight text-violet-600">
            {summary?.requests_this_week ?? "—"}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            This Week
          </p>
          <p className="text-[11px] font-medium text-violet-600 mt-0.5">
            requests created
          </p>
          <Sparkline
            points={
              trendPoints.length >= 2 ? trendPoints : [0, 2, 1, 4, 3, 5, 3, 7]
            }
            color="#9333ea"
          />
        </div>
      </div>

      {/* Aging Overview */}
      {aging && (
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-bold">Request Aging Overview</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                How long requests have been open
              </p>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                Fresh
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Aging
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                Stale
              </span>
            </div>
          </div>

          {/* Health bar */}
          <div className="h-2.5 rounded-full overflow-hidden flex bg-border mb-4">
            <div
              className="h-full bg-green-500 transition-all duration-700"
              style={{ width: `${freshPct}%` }}
            />
            <div
              className="h-full bg-amber-500 transition-all duration-700"
              style={{ width: `${agingPct}%` }}
            />
            <div
              className="h-full bg-red-500 transition-all duration-700"
              style={{ width: `${stalePct}%` }}
            />
          </div>

          {/* Tiles */}
          <div className="grid grid-cols-3 gap-3">
            {/* Fresh */}
            <div className="relative overflow-hidden rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/40 dark:border-green-900 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-green-700 dark:text-green-400">
                Fresh
              </p>
              <p className="text-[28px] font-extrabold leading-tight text-green-900 dark:text-green-300">
                {aging.fresh.count}
              </p>
              <p className="text-[11px] text-green-700 dark:text-green-500">
                {aging.fresh.label}
              </p>
              <div className="absolute -right-3 -bottom-3 h-14 w-14 rounded-full bg-green-500 opacity-20" />
            </div>

            {/* Aging */}
            <div className="relative overflow-hidden rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                Aging
              </p>
              <p className="text-[28px] font-extrabold leading-tight text-amber-900 dark:text-amber-300">
                {aging.aging.count}
              </p>
              <p className="text-[11px] text-amber-700 dark:text-amber-500">
                {aging.aging.label}
              </p>
              <div className="absolute -right-3 -bottom-3 h-14 w-14 rounded-full bg-amber-500 opacity-20" />
            </div>

            {/* Stale */}
            <div className="relative overflow-hidden rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 dark:text-red-400">
                Stale
              </p>
              <p className="text-[28px] font-extrabold leading-tight text-red-900 dark:text-red-300">
                {aging.stale.count}
              </p>
              <p className="text-[11px] text-red-700 dark:text-red-500">
                {aging.stale.label}
              </p>
              <div className="absolute -right-3 -bottom-3 h-14 w-14 rounded-full bg-red-500 opacity-20" />
            </div>
          </div>

          {/* Stale requests list */}
          {aging.stale_requests.length > 0 && (
            <div className="mt-4 border-t border-border pt-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 dark:text-red-400 mb-3">
                Oldest Stale Requests
              </p>
              <div className="space-y-2">
                {aging.stale_requests.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between gap-3 rounded-lg border-l-4 border-red-400 bg-red-50/60 dark:bg-red-950/30 pl-3 pr-3 py-2 cursor-pointer hover:bg-red-100/60 dark:hover:bg-red-950/50 transition-colors"
                    onClick={() => window.open(`/requests/${req.id}`, "_blank")}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-red-900 dark:text-red-300">
                        {req.reference_id}
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-500 truncate">
                        {req.title}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                      {req.days_idle}d
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Funnel + Trend */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Funnel */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <p className="text-sm font-bold mb-4">Request Flow Funnel</p>
          <div className="space-y-2">
            {funnelRows.map((row) => (
              <div key={row.status}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: row.color }}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {row.label}
                  </span>
                </div>
                <div className="relative h-6 rounded bg-muted overflow-hidden">
                  <div
                    className="h-full rounded flex items-center pl-2.5 text-xs font-bold text-white transition-all duration-700"
                    style={{
                      background: row.color,
                      width: `${Math.max(row.widthPct, 8)}%`,
                    }}
                  >
                    {row.count}
                  </div>
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-muted-foreground">
                    {row.pct}% of total
                  </span>
                </div>
              </div>
            ))}
            {funnelRows.length === 0 && (
              <p className="text-xs text-muted-foreground py-6 text-center">
                No data yet
              </p>
            )}
          </div>
        </div>

        {/* Trend */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <p className="text-sm font-bold mb-3">Weekly Trend</p>
          <TrendChart data={trendChartData} />
          <div className="flex justify-center mt-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <svg width="12" height="8" viewBox="0 0 12 8">
                <path
                  d="M0 4 Q3 1 6 4 Q9 7 12 4"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                />
              </svg>
              Requests Created
            </span>
          </div>
        </div>
      </div>

      {/* Team Performance */}
      {podPerf && Object.values(podPerf).some((p) => p.total > 0) && (
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-[15px] font-bold">Team Performance</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Completion rate and throughput by pod
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(podPerf)
              .filter(([, p]) => p.total > 0)
              .map(([pod, perf]) => {
                const color = podColor(pod);
                const label = POD_LABELS[pod as Pod] ?? pod;
                return (
                  <div
                    key={pod}
                    className="rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-px cursor-pointer"
                    onClick={() => {
                      const params = new URLSearchParams({
                        pod,
                        status: "InProgress",
                      });
                      navigate(`/dashboard?${params.toString()}`);
                    }}
                  >
                    {/* Accent stripe */}
                    <div className="h-[3px]" style={{ background: color }} />
                    <div className="flex items-start justify-between gap-2 p-4 pb-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {perf.total} total request
                          {perf.total !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <DonutChart
                        pct={Math.round(perf.completion_percent)}
                        color={color}
                      />
                    </div>
                    <div className="h-px bg-border mx-4" />
                    <div className="grid grid-cols-3 gap-2 px-4 py-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          Cycle
                        </p>
                        <p className="text-sm font-bold mt-0.5 tabular-nums">
                          {perf.cycle_time_days}d
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          In Progress
                        </p>
                        <p className="text-sm font-bold mt-0.5 tabular-nums">
                          {perf.in_progress}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          Velocity
                        </p>
                        <p className="text-sm font-bold mt-0.5 tabular-nums">
                          {perf.velocity_per_week}/wk
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Leadership Analytics ──────────────────────────────────────────────── */}

      {/* Time-to-First-Response + SLA Compliance */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Time-to-First-Response */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 dark:bg-sky-950">
              <Timer className="h-4 w-4 text-sky-600" />
            </div>
            <div>
              <p className="text-sm font-bold">Time to First Response</p>
              <p className="text-xs text-muted-foreground">
                Avg hours from submission to first reviewer action
              </p>
            </div>
            {tfr && (
              <span className="ml-auto text-2xl font-extrabold tabular-nums text-sky-600">
                {tfr.overall_display}
              </span>
            )}
          </div>
          <div className="space-y-2.5">
            {(["Critical", "High", "Medium", "Low"] as const).map((p) => {
              const row = tfr?.by_priority?.[p];
              const slaH = row?.sla_threshold_hours ?? 48;
              const avgH = row?.avg_hours ?? 0;
              const over = row && avgH > slaH;
              const barPct = row ? Math.min((avgH / slaH) * 100, 100) : 0;
              const PRIORITY_COLORS: Record<string, string> = {
                Critical: "#dc2626",
                High: "#f97316",
                Medium: "#eab308",
                Low: "#22c55e",
              };
              const color = PRIORITY_COLORS[p];
              return (
                <div key={p}>
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-[11px] font-bold uppercase tracking-wider"
                      style={{ color }}
                    >
                      {p}
                    </span>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">
                        SLA:{" "}
                        {row?.sla_threshold_hours
                          ? row.sla_threshold_hours < 48
                            ? `${row.sla_threshold_hours}h`
                            : `${row.sla_threshold_hours / 24}d`
                          : "—"}
                      </span>
                      <span
                        className={cn(
                          "font-bold tabular-nums",
                          over ? "text-red-500" : "text-foreground",
                        )}
                      >
                        {row ? row.avg_display : "—"}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: row ? `${Math.max(barPct, 4)}%` : "0%",
                        background: over ? "#ef4444" : color,
                      }}
                    />
                  </div>
                  {row && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {row.responded_count} request
                      {row.responded_count !== 1 ? "s" : ""} responded
                    </p>
                  )}
                </div>
              );
            })}
            {!tfr && (
              <p className="text-xs text-muted-foreground text-center py-4">
                No data yet
              </p>
            )}
          </div>
        </div>

        {/* SLA Compliance */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-950">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-bold">SLA Compliance</p>
              <p className="text-xs text-muted-foreground">
                % of requests responded within SLA threshold
              </p>
            </div>
            {sla?.overall_compliance_pct != null && (
              <span
                className={cn(
                  "ml-auto text-2xl font-extrabold tabular-nums",
                  sla.overall_compliance_pct >= 80
                    ? "text-emerald-600"
                    : sla.overall_compliance_pct >= 60
                      ? "text-amber-500"
                      : "text-red-500",
                )}
              >
                {sla.overall_compliance_pct}%
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {(["Critical", "High", "Medium", "Low"] as const).map((p) => {
              const row = sla?.by_priority?.[p];
              const pct = row?.compliance_pct;
              const PRIORITY_BG: Record<string, string> = {
                Critical:
                  "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900",
                High: "bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-900",
                Medium:
                  "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900",
                Low: "bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900",
              };
              const PRIORITY_TEXT: Record<string, string> = {
                Critical: "text-red-700 dark:text-red-400",
                High: "text-orange-700 dark:text-orange-400",
                Medium: "text-amber-700 dark:text-amber-400",
                Low: "text-green-700 dark:text-green-400",
              };
              return (
                <div
                  key={p}
                  className={cn("rounded-lg border p-3", PRIORITY_BG[p])}
                >
                  <p
                    className={cn(
                      "text-[10px] font-bold uppercase tracking-wider",
                      PRIORITY_TEXT[p],
                    )}
                  >
                    {p}
                  </p>
                  <p className="text-xl font-extrabold tabular-nums mt-0.5">
                    {pct != null ? `${pct}%` : "—"}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    SLA: {row?.sla_label ?? "—"} ·{" "}
                    {row ? `${row.within_sla}/${row.total}` : "0/0"}
                  </p>
                </div>
              );
            })}
          </div>
          {!sla && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No data yet
            </p>
          )}
        </div>
      </div>

      {/* Workload Balance */}
      {workload && Array.isArray(workload.members) && (
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-950">
              <Users className="h-4 w-4 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-bold">Workload Balance</p>
              <p className="text-xs text-muted-foreground">
                Active requests per team member
              </p>
            </div>
            <span className="ml-auto text-xs text-muted-foreground">
              {workload.total_active} active total
            </span>
          </div>
          <div className="space-y-2">
            {[
              ...workload.members,
              ...(workload.unclaimed.total > 0
                ? [
                    {
                      email: "",
                      name: "Unclaimed",
                      total: workload.unclaimed.total,
                      in_progress: 0,
                      awaiting: workload.unclaimed.awaiting,
                    },
                  ]
                : []),
            ].map((m, i) => {
              const barW =
                workload.total_active > 0
                  ? (m.total / workload.total_active) * 100
                  : 0;
              const isUnclaimed = !m.email;
              return (
                <div key={m.email || "unclaimed"}>
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white",
                        isUnclaimed ? "bg-slate-400" : "bg-violet-500",
                      )}
                    >
                      {isUnclaimed
                        ? "?"
                        : m.name
                            .split(" ")
                            .map((n) => n[0])
                            .slice(0, 2)
                            .join("")}
                    </div>
                    <span className="text-xs font-semibold flex-1 truncate">
                      {m.name}
                    </span>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground shrink-0">
                      {m.in_progress > 0 && (
                        <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 px-1.5 py-0.5 font-semibold">
                          {m.in_progress} in-progress
                        </span>
                      )}
                      {m.awaiting > 0 && (
                        <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 font-semibold">
                          {m.awaiting} awaiting
                        </span>
                      )}
                      <span className="font-bold text-foreground w-5 text-right tabular-nums">
                        {m.total}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-700",
                        isUnclaimed ? "bg-slate-400" : "bg-violet-500",
                      )}
                      style={{ width: `${Math.max(barW, 2)}%` }}
                    />
                  </div>
                  {i <
                    workload.members.length +
                      (workload.unclaimed.total > 0 ? 1 : 0) -
                      1 && <div className="mt-2" />}
                </div>
              );
            })}
            {workload.members.length === 0 &&
              workload.unclaimed.total === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No active requests
                </p>
              )}
          </div>
        </div>
      )}

      {/* Defect / Feature Trend Split + Priority Cycle Time */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Type Trend */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-50 dark:bg-fuchsia-950">
              <GitBranch className="h-4 w-4 text-fuchsia-600" />
            </div>
            <div>
              <p className="text-sm font-bold">Feature vs Defect Trend</p>
              <p className="text-xs text-muted-foreground">
                Weekly submission split by request type
              </p>
            </div>
          </div>
          {typeTrend ? (
            <>
              <div className="space-y-1.5">
                {Object.entries(typeTrend).map(([week, counts]) => {
                  const total = counts.Feature + counts.Defect;
                  const featurePct =
                    total > 0 ? (counts.Feature / total) * 100 : 0;
                  return (
                    <div key={week}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground w-6">
                          {week}
                        </span>
                        <div className="flex gap-2 text-[10px] text-muted-foreground">
                          <span className="text-fuchsia-600 font-semibold">
                            {counts.Feature}F
                          </span>
                          <span className="text-orange-500 font-semibold">
                            {counts.Defect}D
                          </span>
                        </div>
                      </div>
                      <div className="h-3 rounded-full bg-muted overflow-hidden flex">
                        {total > 0 ? (
                          <>
                            <div
                              className="h-full bg-fuchsia-500 transition-all duration-700"
                              style={{ width: `${featurePct}%` }}
                            />
                            <div
                              className="h-full bg-orange-400 transition-all duration-700"
                              style={{ width: `${100 - featurePct}%` }}
                            />
                          </>
                        ) : (
                          <div className="h-full w-full bg-muted-foreground/10 rounded-full" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center gap-4 mt-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-fuchsia-500" />
                  Feature
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-orange-400" />
                  Defect
                </span>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              No data yet
            </p>
          )}
        </div>

        {/* Priority Cycle Time */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-950">
              <Clock className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-bold">Priority Cycle Time</p>
              <p className="text-xs text-muted-foreground">
                Avg days to close a request by priority
              </p>
            </div>
          </div>
          {cycleTime ? (
            <div className="space-y-3">
              {(["Critical", "High", "Medium", "Low"] as const).map((p) => {
                const row = cycleTime.by_priority?.[p];
                const avg = row?.avg_days;
                const maxAll = Math.max(
                  ...Object.values(cycleTime.by_priority ?? {}).map(
                    (r) => r.avg_days ?? 0,
                  ),
                  1,
                );
                const barPct = avg != null ? (avg / maxAll) * 100 : 0;
                const PRIORITY_COLORS: Record<string, string> = {
                  Critical: "#dc2626",
                  High: "#f97316",
                  Medium: "#eab308",
                  Low: "#22c55e",
                };
                const color = PRIORITY_COLORS[p];
                return (
                  <div key={p}>
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-[11px] font-bold uppercase tracking-wider"
                        style={{ color }}
                      >
                        {p}
                      </span>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {row && row.sample_size > 0 ? (
                          <>
                            <span>
                              {row.min_days}–{row.max_days}d range
                            </span>
                            <span className="font-bold text-foreground tabular-nums">
                              {avg}d avg
                            </span>
                          </>
                        ) : (
                          <span>No closed requests</span>
                        )}
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: avg != null ? `${Math.max(barPct, 4)}%` : "0%",
                          background: color,
                        }}
                      />
                    </div>
                    {row && row.sample_size > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {row.sample_size} closed request
                        {row.sample_size !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              No data yet
            </p>
          )}
        </div>
      </div>

      {/* Escalations */}
      {escalations && escalations.total > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-5 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-900 dark:text-red-300">
                Escalations
              </p>
              <p className="text-xs text-red-700 dark:text-red-500">
                {escalations.total} request{escalations.total !== 1 ? "s" : ""}{" "}
                stuck &gt;
                {escalations.threshold_days} days
              </p>
            </div>
          </div>
          <div className="space-y-2.5">
            {escalations.escalations.slice(0, 5).map((esc, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex items-start justify-between gap-3",
                  "rounded-lg border-l-4 border-red-500 pl-3 pr-3 py-2",
                  "bg-red-100/60 dark:bg-red-900/30",
                )}
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-red-900 dark:text-red-300">
                    {esc.reference_id}: {esc.title}
                  </p>
                  <p className="text-[11px] text-red-700 dark:text-red-500 mt-0.5">
                    {esc.status} · {esc.days_stuck} days
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                  {esc.days_stuck}d
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
